import React, { useEffect } from "react";
import { View } from "react-native";
import Animated, {
  type SharedValue,
  useSharedValue,
  withRepeat,
  withSequence,
  withTiming,
} from "react-native-reanimated";

import Text from "$src/core/components/atoms/Text/Text";

import $, {
  DOT_RESET_PHASE,
  useListeningDotAnimatedStyle,
  useListeningIndicatorAnimatedStyles,
} from "./ListeningIndicator.styles";

const DOT_COUNT = 3;
const DOT_CYCLE_MS = 400;

type DotProps = {
  index: number;
  activeDot: SharedValue<number>;
};

const ListeningDot = ({ index, activeDot }: DotProps) => {
  const dotStyle = useListeningDotAnimatedStyle({ index, activeDot });

  return (
    <Text.Animated
      style={[$.dot, dotStyle]}
      accessibilityRole="text"
      accessibilityElementsHidden
      importantForAccessibility="no"
    >
      .
    </Text.Animated>
  );
};

type Props = {
  label: string;
  audioLevel: number;
};

const ListeningIndicator = ({ label, audioLevel }: Props) => {
  const activeDot = useSharedValue(0);
  const level = useSharedValue(0);

  const { containerStyle } = useListeningIndicatorAnimatedStyles({ level });

  /* Loop the ellipsis dot highlight animation. */
  useEffect(() => {
    activeDot.value = withRepeat(
      withSequence(
        withTiming(0, { duration: DOT_CYCLE_MS }),
        withTiming(1, { duration: DOT_CYCLE_MS }),
        withTiming(2, { duration: DOT_CYCLE_MS }),
        withTiming(DOT_RESET_PHASE, { duration: DOT_CYCLE_MS }),
      ),
      -1,
      false,
    );
  }, [activeDot]);

  /* Smooth the speech-reactive scale pulse from volume events. */
  useEffect(() => {
    level.value = withTiming(audioLevel, { duration: 100 });
  }, [audioLevel, level]);

  return (
    <Animated.View style={[$.container, containerStyle]} pointerEvents="none">
      <Text
        preset="span"
        style={$.label}
        accessibilityRole="text"
        accessibilityLiveRegion="polite"
      >
        {label}
      </Text>
      <View
        style={$.dots}
        accessibilityElementsHidden
        importantForAccessibility="no-hide-descendants"
      >
        {Array.from({ length: DOT_COUNT }, (_, index) => (
          <ListeningDot key={index} index={index} activeDot={activeDot} />
        ))}
      </View>
    </Animated.View>
  );
};

export default ListeningIndicator;
